<script lang="ts">
  import { onMount } from "svelte";
  import { getTokenStorage } from "../utils/localStorage";
  import { appStore } from "../stores/appStore";
  import Navbar from "../components/Navbar.svelte";
  import { AppApiClient } from "../api_contracts/app.client";
  import { UserProfile } from "../api_contracts/app";

  // Form state
  let owner = $state("");
  let profile: UserProfile | null = $state(null);
  let repositoryName = $state("");
  let description = $state("");
  let copyMasterBranchOnly = $state(true);

  // Repository name validation
  let isCheckingName = $state(false);
  let nameAvailable = $state<boolean | null>(null);
  let nameCheckMessage = $state("");
  let checkNameTimeout: ReturnType<typeof setTimeout> | null = null;

  // Original repository info
  let originalOwner = $state("");
  let originalRepoName = $state("");
  let originalRepoId = $state("");

  // Client and token state
  let client: AppApiClient | null = $state(null);
  let token: string = $state("");
  let loading: boolean = $state(false);
  let error: string = $state("");
  let fetchingProfile: boolean = $state(true);
  let fetchingRepoDetails: boolean = $state(true);

  // Subscribe to store
  appStore.subscribe((state) => {
    profile = state.profile;
    client = state.client;
    token = state.token;
  });

  // Character counter for description
  let descriptionLength = $derived(description.length);
  const maxDescriptionLength = 350;

  // Form validation
  let isAuthenticated = $state(false);

  $effect(() => {
    const storedToken = getTokenStorage();
    isAuthenticated = !!storedToken;
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      window.location.hash = "/login";
    }
  });

  // Fetch user profile to get username
  async function fetchUserProfile() {
    if (!client || !token) {
      fetchingProfile = false;
      return;
    }

    try {
      const result = await client.getUserProfile({ token });

      if (result.response.response.successFull) {
        profile = result.response.profile;
        owner = result.response.profile?.userName || "";
      } else {
        console.error(
          "Failed to fetch user profile:",
          result.response.response.reason,
        );
        owner = "username"; // Fallback
      }
    } catch (e: any) {
      console.error("Error fetching user profile:", e);
      owner = "username"; // Fallback
    } finally {
      fetchingProfile = false;
    }
  }

  // Fetch repository details
  async function fetchRepositoryDetails() {
    if (!client) {
      fetchingRepoDetails = false;
      return;
    }

    try {
      // Extract username and repo name from URL: /users/:username/repositories/:name/fork
      const hash = window.location.hash;
      const match = hash.match(/\/users\/([^\/]+)\/repositories\/([^\/]+)\/fork/);

      if (!match) {
        error = "Invalid fork URL";
        fetchingRepoDetails = false;
        return;
      }

      originalOwner = match[1];
      originalRepoName = match[2];

      // Set default values
      repositoryName = originalRepoName;

      // Fetch repository details from API
      const result = await client.getRepositoryDetails({
        token: token || "",
        ownerId: originalOwner,
        repositoryName: originalRepoName,
      });

      if (result.response.successFull && result.response.repository) {
        description = result.response.repository.description || "";
        originalRepoId = result.response.repository.id || "";
      } else {
        console.error(
          "Failed to fetch repository details:",
          result.response.reason,
        );
        error = result.response.reason || "Failed to load repository details";
      }
    } catch (e: any) {
      console.error("Error fetching repository details:", e);
    } finally {
      fetchingRepoDetails = false;
    }
  }

  // Check if repository name is available
  async function checkRepositoryNameAvailability(name: string) {
    if (!client || !owner || !name.trim()) {
      nameAvailable = null;
      nameCheckMessage = "";
      return;
    }

    const sanitizedName = name.trim().replace(/\s+/g, '-');
    if (!sanitizedName) {
      nameAvailable = null;
      nameCheckMessage = "";
      return;
    }

    try {
      isCheckingName = true;
      const result = await client.getRepositoryDetails({
        token: token || "",
        ownerId: owner,
        repositoryName: sanitizedName,
      });

      // If the repository is found, name is not available
      if (result.response.successFull && result.response.repository) {
        nameAvailable = false;
        nameCheckMessage = `Repository "${sanitizedName}" already exists`;
      } else {
        // Repository not found, name is available
        nameAvailable = true;
        nameCheckMessage = `"${sanitizedName}" is available`;
      }
    } catch (e: any) {
      // On error, assume the repository doesn't exist (name is available)
      nameAvailable = true;
      nameCheckMessage = `"${sanitizedName}" is available`;
    } finally {
      isCheckingName = false;
    }
  }

  // Watch for repository name changes and check availability with debouncing
  $effect(() => {
    if (repositoryName && owner) {
      // Clear previous timeout
      if (checkNameTimeout) {
        clearTimeout(checkNameTimeout);
      }

      // Reset validation state immediately
      nameAvailable = null;
      nameCheckMessage = "";

      // Set new timeout for debounced check
      checkNameTimeout = setTimeout(() => {
        checkRepositoryNameAvailability(repositoryName);
      }, 500); // 500ms debounce
    }

    // Cleanup function
    return () => {
      if (checkNameTimeout) {
        clearTimeout(checkNameTimeout);
      }
    };
  });

  onMount(() => {
    const storedToken = getTokenStorage();
    if (storedToken) {
      if (!profile) {
        fetchUserProfile();
      } else {
        owner = profile.userName || "";
        fetchingProfile = false;
      }
      fetchRepositoryDetails();
    } else {
      fetchingProfile = false;
      fetchingRepoDetails = false;
    }
  });

  async function handleSubmit(event: Event) {
    event.preventDefault();

    // Validate required fields
    if (!repositoryName.trim()) {
      alert("Please enter a repository name");
      return;
    }

    const sanitizedRepoName = repositoryName.trim().replace(/\s+/g, '-');

    if (!sanitizedRepoName) {
      alert("Please enter a repository name");
      return;
    }

    if (!client) {
      error = "gRPC client not initialized";
      alert("Error: gRPC client not initialized. Please refresh the page.");
      return;
    }

    if (!token) {
      error = "No authentication token";
      alert("Error: Not authenticated. Please login again.");
      window.location.hash = "/login";
      return;
    }

    if (!originalRepoId) {
      error = "Original repository not found";
      alert("Error: Original repository not found.");
      return;
    }

    try {
      loading = true;
      error = "";

      const response = await client.forkRepository({
        token,
        repositoryId: originalRepoId
      });

      if (response.response?.successFull) {
        console.log("Repository forked successfully:", response.response);
        // Redirect to forked repository page
        window.location.hash = `/users/${owner}/repositories/${sanitizedRepoName}`;
      } else {
        error = response.response?.reason || "Failed to fork repository";
        alert(`Error: ${error}`);
      }
    } catch (e: any) {
      error = e.message || "Error forking repository";
      console.error("Error:", e);
      alert(`Error forking repository: ${error}`);
    } finally {
      loading = false;
    }
  }
</script>

<Navbar />

<div
  class="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-200"
>
  <div class="container mx-auto px-4 py-8 max-w-4xl">
    <h1 class="text-2xl font-normal mb-1 text-gray-900 dark:text-gray-100">
      Create a new fork
    </h1>
    <p class="text-gray-600 dark:text-gray-400 text-sm mb-6">
      A <span class="font-semibold">fork</span> is a copy of a repository. Forking a repository allows you to freely experiment with changes without
      affecting the original project.
      <br />
      <br />
      Required fields are marked with an asterisk (*).
    </p>

    {#if fetchingRepoDetails}
      <div class="flex items-center justify-center py-12">
        <div
          class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"
        ></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Loading repository details...</span>
      </div>
    {:else if error && !originalRepoId}
      <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <h3 class="text-red-800 dark:text-red-200 font-medium mb-2">Failed to load repository</h3>
        <p class="text-red-600 dark:text-red-400 text-sm">{error}</p>
        <p class="text-red-600 dark:text-red-400 text-sm mt-2">
          Please check the URL and make sure the repository exists and you have access to it.
        </p>
      </div>
    {:else}
      <form class="space-y-6" onsubmit={handleSubmit}>
        <!-- Owner -->
        <div class="flex flex-col md:flex-row md:items-start">
          <label
            for="owner"
            class="text-sm font-medium w-full md:w-1/4 mb-1 md:mb-0 pt-2"
          >
            Owner <span class="text-red-500">*</span>
          </label>
          <div class="w-full md:w-3/4">
            <div
              class="flex items-center p-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-800 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {#if fetchingProfile}
                <div
                  class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"
                ></div>
                <span class="text-gray-500 dark:text-gray-400">Loading...</span>
              {:else}
                <div
                  class="w-5 h-5 rounded-full mr-2 bg-indigo-600 flex items-center justify-center text-white text-xs font-semibold"
                >
                  {owner ? owner.charAt(0).toUpperCase() : "U"}
                </div>
                <span class="text-gray-900 dark:text-gray-200 font-medium"
                  >{owner || "username"}</span
                >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4 ml-auto text-gray-500 dark:text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              {/if}
            </div>
            <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
              By default, forks are named the same as their upstream repository. You can customize the name to distinguish it further.
            </p>
          </div>
        </div>

        <!-- Repository Name with / separator -->
        <div class="flex flex-col md:flex-row md:items-start">
          <label
            for="repo-name"
            class="text-sm font-medium w-full md:w-1/4 mb-1 md:mb-0 pt-2"
          >
            Repository name <span class="text-red-500">*</span>
          </label>
          <div class="w-full md:w-3/4">
            <div class="flex items-center">
              <span class="text-gray-500 dark:text-gray-400 mr-2 text-lg">/</span>
              <div class="flex-1 relative">
                <input
                  type="text"
                  id="repo-name"
                  bind:value={repositoryName}
                  class="w-full px-3 py-2 border {nameAvailable === false ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : nameAvailable === true ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : 'border-gray-300 dark:border-gray-700 focus:border-blue-500 focus:ring-blue-500'} rounded-md bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-colors"
                  placeholder="e.g. my-awesome-project"
                  required
                />
                {#if isCheckingName}
                  <div class="absolute right-3 top-1/2 -translate-y-1/2">
                    <div class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 dark:border-gray-400"></div>
                  </div>
                {/if}
              </div>
            </div>
            {#if nameCheckMessage}
              <p class="text-xs mt-1 ml-6 {nameAvailable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}">
                {nameCheckMessage}
              </p>
            {/if}
          </div>
        </div>

        <!-- Description -->
        <div class="flex flex-col md:flex-row">
          <label
            for="description"
            class="text-sm font-medium w-full md:w-1/4 mb-1 md:mb-0 pt-2"
          >
            Description <span class="text-gray-500">(optional)</span>
          </label>
          <div class="w-full md:w-3/4">
            <textarea
              id="description"
              rows="3"
              bind:value={description}
              maxlength={maxDescriptionLength}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors resize-y"
              placeholder="A short description of your fork"
            ></textarea>
            <p class="text-xs text-gray-600 dark:text-gray-400 text-right mt-1">
              {descriptionLength} / {maxDescriptionLength} characters
            </p>
          </div>
        </div>

        <!-- Copy the master branch only -->
        <div class="flex items-start pt-4 border-t border-gray-300 dark:border-gray-700">
          <label class="flex items-start cursor-pointer">
            <input
              type="checkbox"
              bind:checked={copyMasterBranchOnly}
              class="mt-1 h-4 w-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
            />
            <div class="ml-3">
              <span class="text-sm font-medium text-gray-900 dark:text-gray-200">
                Copy the <span class="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-mono text-xs">master</span> branch only
              </span>
              <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Contribute back to <span class="font-medium">{originalOwner}/{originalRepoName}</span> by adding your own branch.
                <a href="#" class="text-blue-500 dark:text-blue-400 hover:underline">Learn more</a>.
              </p>
            </div>
          </label>
        </div>

        <!-- Submit Button -->
        <div class="pt-6 border-t border-gray-300 dark:border-gray-700">
          <button
            type="submit"
            disabled={loading || fetchingProfile || fetchingRepoDetails || isCheckingName || nameAvailable === false}
            class="py-2 px-4 rounded-md bg-green-600 hover:bg-green-700 text-white font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? "Creating fork..." : "Create fork"}
          </button>
          {#if nameAvailable === false && !isCheckingName}
            <p class="text-xs text-red-600 dark:text-red-400 mt-2">
              Please choose a different repository name
            </p>
          {/if}
        </div>
      </form>
    {/if}
  </div>
</div>
